/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { OrderOriginator, SalesOrdersDataActions, OrderOriginatorSearchCriteria } from '@msdyn365-commerce/retail-proxy';
import { buildCacheKey } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Action input.
 */
export class GetOrderOriginatorsInput implements IActionInput {
    public searchCriteria: OrderOriginatorSearchCriteria = {};

    private readonly apiSettings: ICommerceApiSettings;

    public constructor(transactionIds: string[] | undefined, salesIds: string[] | undefined, apiSettings: ICommerceApiSettings) {
        this.searchCriteria = {
            TransactionIds: transactionIds,
            SalesIds: salesIds
        };
        this.apiSettings = apiSettings;
    }

    /**
     * GetCacheKey.
     * @returns - String.
     */
    public getCacheKey = (): string =>
        buildCacheKey(`OrderOriginators-${this.searchCriteria.TransactionIds}-${this.searchCriteria.SalesIds}`, this.apiSettings);

    /**
     * GetCacheObjectType.
     * @returns - String.
     */
    public getCacheObjectType = (): string => 'OrderOriginators';

    /**
     * DataCacheType.
     * @returns - String.
     */
    public dataCacheType = (): CacheType => 'request';
}

/**
 * Get order originators by transaction ids or sales ids action.
 * @param input - GetOrderOriginators input.
 * @param context - Channel context.
 * @returns - Originators array.
 */
export async function getOriginatorsAction(input: GetOrderOriginatorsInput, context: IActionContext): Promise<OrderOriginator[]> {
    return SalesOrdersDataActions.getOrderOriginatorsAsync({ callerContext: context }, input.searchCriteria);
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData -- InputData as ICreateActionContext.
 * @returns Action Input.
 */
export const createSalesOrderOriginatorsInput = (inputData: ICreateActionContext) => {
    const { salesId, transactionId } = inputData.requestContext.query ?? {};

    if (salesId) {
        return new GetOrderOriginatorsInput(undefined, [salesId], inputData.requestContext.apiSettings);
    } else if (transactionId) {
        return new GetOrderOriginatorsInput([transactionId], undefined, inputData.requestContext.apiSettings);
    }

    throw new Error('createSalesOrderOriginatorsInput - No salesId or transactionId provided.');
};

/**
 * GetSalesOrderOriginatorsActionDataAction.
 * @returns -- IObservableAction<OrderOriginator[]>.
 */
export const getSalesOrderOriginatorsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/order-management/get-sales-order-originators',
    action: getOriginatorsAction as IAction<OrderOriginator[]>,
    input: createSalesOrderOriginatorsInput
});

export default getSalesOrderOriginatorsActionDataAction;
